import { Link } from "react-router";
import { useMemo, type ReactNode } from "react";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { ExportButton } from "@/components/admin/export-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Badge } from "@/components/ui/badge";
import { Film, Star } from "lucide-react";

import { TopToolbar } from "../layout/TopToolbar";
import type { EchoflicksVideoSession } from "../types";
import { H3LocalBadge } from "./H3LocalBadge";
import { LtxLocalBadge } from "./LtxLocalBadge";
import { TestEchoBadge } from "./TestEchoBadge";
import { WebEchoBadge } from "./WebEchoBadge";
import { EchoLanguageBadge } from "./EchoLanguageBadge";
import { echoLanguageFilter } from "./echoLanguageFilter";
import { isStudioFilm } from "./studioFilms";
import {
  StudioCreatorCell,
  StudioDirectoryProvider,
  StudioFilmUserCell,
} from "./StudioFilmCells";
import { useStudioDirectoryContext } from "./studioDirectoryContext";
import { studioAgencyChoices, studioFilmFilters } from "./studioFilmFilters";
import {
  confidenceLabel,
  sessionStatusLabel,
  statusBadgeVariant,
} from "./sessionUtils";

const statusChoices = [
  { id: "completed", name: "Completed" },
  { id: "scripted", name: "Scripted" },
  { id: "media_received", name: "Media received" },
  { id: "unknown", name: "Unknown" },
];

const confidenceChoices = [
  { id: "exact", name: "Exact" },
  { id: "inferred", name: "Inferred" },
  { id: "user_level", name: "User level" },
  { id: "unknown", name: "Unknown" },
];

// `echo_origin` is the dashboard-API's test-echo marker: "organic" asks for rows
// with no origin (real customer Echoes), "test_autoqueue" for the synthetic admin
// runs. Left empty by default — a test Echo stays visible until an operator opts
// into hiding it, so nothing ever disappears unexpectedly (plan §3.5-C).
const echoOriginChoices = [
  { id: "organic", name: "Hide test echoes" },
  { id: "test_autoqueue", name: "Only test echoes" },
];

const sessionFilters = [
  <SearchInput source="q" alwaysOn />,
  <SelectInput
    source="status"
    label="Status"
    emptyText="Any status"
    choices={statusChoices}
    optionText="name"
    optionValue="id"
  />,
  <SelectInput
    source="source_confidence"
    label="Confidence"
    emptyText="Any confidence"
    choices={confidenceChoices}
    optionText="name"
    optionValue="id"
  />,
  <SelectInput
    source="echo_origin"
    label="Test echoes"
    emptyText="Show test echoes"
    choices={echoOriginChoices}
    optionText="name"
    optionValue="id"
  />,
  echoLanguageFilter(),
  <SelectInput
    source="surface"
    label="Web surface"
    emptyText="Any surface"
    choices={[
      { id: "partner_demo", name: "Partner demo" },
      { id: "my", name: "my.echoflicks.com" },
    ]}
    optionText="name"
    optionValue="id"
  />,
  <TextInput source="poc_collection" label="POC collection" />,
];

export const EchoflicksSessionList = () => {
  return (
    // B6.3: Studio films show their agency and creator, not the phantom
    // `studio-…` contact (the agencies' "Films" links land here).
    <StudioDirectoryProvider>
      <SessionListView />
    </StudioDirectoryProvider>
  );
};

const SessionListView = () => {
  const directory = useStudioDirectoryContext();
  const filters = useMemo(
    () => [
      ...sessionFilters,
      ...studioFilmFilters(studioAgencyChoices(directory)),
    ],
    [directory],
  );
  return (
    <List
      title="Video Sessions"
      perPage={25}
      sort={{ field: "display_date", order: "DESC" }}
      filters={filters}
      actions={<SessionListActions />}
    >
      <DataTable<EchoflicksVideoSession>
        rowClick="show"
        bulkActionButtons={false}
      >
        <DataTable.Col
          source="movie_title"
          label="Session"
          render={(record) => (
            <SessionTitle record={record as EchoflicksVideoSession} />
          )}
        />
        <DataTable.Col
          source="contact_name"
          label="User"
          render={(record) => (
            <UserCell record={record as EchoflicksVideoSession} />
          )}
        />
        <DataTable.Col
          source="studio_created_by"
          label="Creator"
          disableSort
          render={(record) => (
            <StudioCreatorCell record={record as EchoflicksVideoSession} />
          )}
        />
        <DataTable.Col
          source="status"
          label="Status"
          render={(record) => (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={statusBadgeVariant(record.status)}>
                {sessionStatusLabel(record.status)}
              </Badge>
              <WebEchoBadge record={record as EchoflicksVideoSession} />
              <TestEchoBadge record={record as EchoflicksVideoSession} />
              <EchoLanguageBadge record={record as EchoflicksVideoSession} />
              <H3LocalBadge
                summary={
                  (record as EchoflicksVideoSession).i2v_provider_summary
                }
                compact
              />
              <LtxLocalBadge
                summary={
                  (record as EchoflicksVideoSession).i2v_provider_summary
                }
                compact
              />
            </div>
          )}
        />
        <DataTable.Col
          source="source_confidence"
          label="Confidence"
          render={(record) => (
            <Badge variant="outline">
              {confidenceLabel(record.source_confidence)}
            </Badge>
          )}
        />
        <DataTable.Col
          source="media_count"
          label="Media"
          render={(record) => (
            <span>
              {record.uploaded_media_count ?? 0} uploaded /{" "}
              {record.media_count ?? 0} total
            </span>
          )}
        />
        <DataTable.Col
          source="rating"
          label="Rating"
          render={(record) => <Rating rating={record.rating} />}
        />
        <DataTable.Col source="display_date" label="Date">
          <DateField source="display_date" showTime />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

const SessionListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

const SessionTitle = ({ record }: { record: EchoflicksVideoSession }) => {
  const title = record.movie_title ?? record.script_title ?? "Untitled session";
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
        <Film className="size-5 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <div className="font-medium truncate">{title}</div>
        <div className="text-xs text-muted-foreground truncate">
          {record.session_id ?? record.source_id}
        </div>
      </div>
    </div>
  );
};

const UserCell = ({ record }: { record: EchoflicksVideoSession }) => {
  if (isStudioFilm(record)) return <StudioFilmUserCell record={record} />;
  const label = record.contact_name || record.whatsapp_user_id;
  const content = (
    <div className="min-w-0">
      <div className="font-medium truncate">{label}</div>
      <div className="text-xs text-muted-foreground">
        {record.whatsapp_user_id}
      </div>
    </div>
  );

  if (!record.contact_id) return content;

  return (
    <Link
      to={`/contacts/${record.contact_id}/show`}
      className="block no-underline hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {content}
    </Link>
  );
};

export const Rating = ({ rating }: { rating?: number | null }) => {
  if (!rating) {
    return <span className="text-muted-foreground">No rating</span>;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Star className="size-4 fill-current" />
      {rating}/5
    </span>
  );
};

export const EmptyHint = ({ children }: { children: ReactNode }) => (
  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
    {children}
  </div>
);
